import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, EmptyState, ErrorState, Panel, Pill, SelectField, Skeleton, TextField } from "../../components/ui.tsx";
import { ConfirmDialog } from "../../components/dialog.tsx";
import { applyFieldErrors, get, patch, post, type ApiError } from "../../lib/api.ts";
import { planKey, usePlan } from "../../lib/plan.tsx";
import { useSession } from "../../lib/session.tsx";
import { roleLabel, type Group, type Invitation, type Membership } from "../../lib/types.ts";
import { formatInstantBR } from "../../../shared/dates.ts";
import { emailSchema } from "../../../shared/auth.ts";
import { useCommand } from "./shared.tsx";

const inviteForm = z.object({ email: emailSchema, role: z.enum(["participant", "instructor"]), group_ids: z.array(z.string()) });

const invStatus: Record<Invitation["status"], { label: string; tone: "green" | "gold" | "gray" | "red" }> = {
  pending: { label: "Pendente", tone: "gold" },
  accepted: { label: "Aceito", tone: "green" },
  revoked: { label: "Revogado", tone: "gray" },
  expired: { label: "Expirado", tone: "red" },
};

export function ParticipantsTab() {
  const plan = usePlan();
  const { user } = useSession();
  const ms = useQuery({ queryKey: planKey(plan.id, "memberships"), queryFn: async () => (await get<{ data: Membership[] }>(`/plans/${plan.id}/memberships?page_size=100`)).data });
  const invs = useQuery({ queryKey: planKey(plan.id, "invitations"), queryFn: async () => (await get<{ data: Invitation[] }>(`/plans/${plan.id}/invitations?page_size=100`)).data });
  const groups = useQuery({ queryKey: planKey(plan.id, "groups"), queryFn: async () => (await get<{ data: Group[] }>(`/plans/${plan.id}/groups`)).data });
  const { run, busy } = useCommand(plan.id);
  const [change, setChange] = useState<{ m: Membership; body: { role?: string; active?: boolean }; title: string; text: string } | null>(null);

  const form = useForm<z.input<typeof inviteForm>>({ resolver: zodResolver(inviteForm), defaultValues: { email: "", role: "participant", group_ids: [] } });
  const invite = form.handleSubmit(async (v) => {
    const ok = await run(async () => {
      const res = await post<{ data: Invitation }>(`/plans/${plan.id}/invitations`, v);
      if (res.data.delivery_status === "failed") throw new Error("O convite foi registrado, mas o e-mail não foi enviado. Use “Reenviar” na lista.");
      return res;
    }, "Convite enviado.", (e: ApiError) => {
      applyFieldErrors(e, form.setError as never);
      return e.fieldErrors.length > 0;
    });
    if (ok) form.reset({ email: "", role: v.role, group_ids: [] });
    else void invs.refetch();
  });

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
      <div className="min-w-0 space-y-5">
        <section>
          <h2 className="mb-2 text-lg font-bold">Participantes</h2>
          {ms.isPending ? <Skeleton /> : ms.isError ? <ErrorState error={ms.error} onRetry={() => void ms.refetch()} /> : (
            <ul className="divide-y divide-line overflow-hidden rounded-panel border border-line bg-surface shadow-e1">
              {ms.data.map((m) => {
                // The plan must keep one active instructor (R16): don't offer an action the server will refuse (UX-22).
                const lastInstructor = m.active && m.role === "instructor" && ms.data.filter((x) => x.active && x.role === "instructor").length === 1;
                return (
                <li key={m.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold">{m.name} {!m.active && <Pill tone="gray">Acesso removido</Pill>}</p>
                    <p className="truncate text-xs text-muted">{m.email} · {roleLabel[m.role]} · {m.groups.length ? m.groups.map((g) => g.name).join(", ") : "sem grupo"}</p>
                    {lastInstructor && <p className="text-xs text-muted">Único instrutor ativo: a oficina precisa de pelo menos um.</p>}
                  </div>
                  {m.active && (
                    <Button variant="secondary" disabled={lastInstructor} onClick={() => setChange({
                      m, body: { role: m.role === "instructor" ? "participant" : "instructor" },
                      title: m.role === "instructor" ? "Tornar participante?" : "Tornar instrutor?",
                      text: m.role === "instructor" ? `${m.name} deixará de administrar e revisar a oficina.` : `${m.name} poderá configurar, editar tudo e revisar iniciativas.`,
                    })}>{m.role === "instructor" ? "Tornar participante" : "Tornar instrutor"}</Button>
                  )}
                  {m.user_id !== user?.id && !lastInstructor && (
                    <Button variant={m.active ? "dangerGhost" : "ghost"} onClick={() => setChange({
                      m, body: { active: !m.active },
                      title: m.active ? "Remover acesso?" : "Restaurar acesso?",
                      text: m.active ? `${m.name} deixará de acessar a oficina na próxima requisição. Os dados são preservados.` : `${m.name} voltará a acessar a oficina, respeitando o prazo de acesso.`,
                    })}>{m.active ? "Remover acesso" : "Restaurar acesso"}</Button>
                  )}
                </li>
                );
              })}
            </ul>
          )}
        </section>
        <section>
          <h2 className="mb-2 text-lg font-bold">Convites</h2>
          {invs.isPending ? <Skeleton /> : invs.isError ? <ErrorState error={invs.error} onRetry={() => void invs.refetch()} /> : invs.data.length === 0 ? (
            <EmptyState title="Nenhum convite enviado ainda." />
          ) : (
            <ul className="divide-y divide-line overflow-hidden rounded-panel border border-line bg-surface shadow-e1">
              {invs.data.map((i) => (
                <li key={i.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 font-semibold">
                      <span className="truncate">{i.email}</span>
                      <Pill tone={invStatus[i.status].tone}>{invStatus[i.status].label}</Pill>
                      {i.status === "pending" && i.delivery_status === "failed" && <Pill tone="red">E-mail não enviado</Pill>}
                    </p>
                    <p className="text-xs text-muted">
                      {roleLabel[i.role]} · {i.groups.length ? i.groups.map((g) => g.name).join(", ") : "sem grupo"} · válido até {formatInstantBR(i.expires_at)}
                    </p>
                  </div>
                  {(i.status === "pending" || i.status === "expired") && (
                    <>
                      <Button variant="secondary" loading={busy} onClick={() => void run(() => post(`/plans/${plan.id}/invitations/${i.id}/reissue`), "Convite reenviado. O link anterior deixou de valer.")}>Reenviar</Button>
                      <Button variant="dangerGhost" onClick={() => void run(() => post(`/plans/${plan.id}/invitations/${i.id}/revoke`), "Convite revogado.")}>Revogar</Button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      <Panel className="h-fit">
        <h2 className="text-lg font-bold">Convidar pessoa</h2>
        <p className="mt-1 text-xs text-muted">O convite vale por 7 dias. A pessoa cria a própria conta ou entra com a conta que já tem.</p>
        <form noValidate onSubmit={invite}>
          <TextField label="E-mail" type="email" required error={form.formState.errors.email?.message} {...form.register("email")} />
          <SelectField label="Papel" {...form.register("role")}>
            <option value="participant">Participante</option>
            <option value="instructor">Instrutor</option>
          </SelectField>
          <fieldset className="my-3">
            <legend className="mb-1.5 text-xs font-bold">Grupos</legend>
            {(groups.data ?? []).filter((g) => !g.archived_at).length === 0 ? (
              <p className="text-xs text-muted">Nenhum grupo ainda. A pessoa poderá ser incluída em grupos depois.</p>
            ) : (
              (groups.data ?? []).filter((g) => !g.archived_at).map((g) => (
                <label key={g.id} className="flex items-center gap-2 py-1 text-sm">
                  <input type="checkbox" value={g.id} className="size-4 accent-primary" {...form.register("group_ids")} />
                  {g.name}
                </label>
              ))
            )}
            {form.formState.errors.group_ids && <p role="alert" className="mt-1 text-xs text-danger-ink">{form.formState.errors.group_ids.message}</p>}
          </fieldset>
          <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>Enviar convite</Button>
        </form>
      </Panel>
      <ConfirmDialog open={change !== null} onOpenChange={(o) => !o && setChange(null)} title={change?.title ?? ""} confirmLabel="Confirmar" loading={busy}
        onConfirm={async () => {
          await run(() => patch(`/plans/${plan.id}/memberships/${change!.m.id}`, { ...change!.body, expected_version: change!.m.version }), "Participante atualizado.");
          setChange(null);
        }}>
        <p className="text-sm">{change?.text}</p>
      </ConfirmDialog>
    </div>
  );
}
